import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CheckCircle,
  CreditCard,
  Shield,
  Clock,
  ArrowRight,
  Calendar,
  Lock,
  ChevronLeft,
  HelpCircle,
  FileText,
  Mail,
  Phone,
  AlertCircle,
  Award,
} from "lucide-react";
import subscriptionService from "../../../services/subscriptionService";

// Composant pour le badge de sécurité
const SecurityBadge = () => (
  <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
    <Lock className="w-4 h-4 text-green-600" />
    <span>Paiement 100% sécurisé par Stripe - Vos données sont chiffrées</span>
  </div>
);

const SubscriptionCheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [planFeatures, setPlanFeatures] = useState({
    included: [],
    limits: [],
    params: [],
  });

  // Mot-clé pour "Illimité"
  const UNLIMITED_KEYWORD = "Illimité";

  // Récupérer les données du plan depuis l'état de navigation
  const {
    plan,
    billingCycle,
    planFeatures: passedFeatures,
  } = location.state || {};

  // Rediriger si pas de plan sélectionné
  useEffect(() => {
    if (!plan) {
      navigate("/client/profil/subscription");
    } else {
      setPlanDetails(plan);
      if (passedFeatures) {
        setPlanFeatures(passedFeatures);
      }
    }
  }, [plan, navigate, passedFeatures]);

  if (!plan || !planDetails) return null;

  // Formatage du prix en dollars
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Calcul du prix selon le cycle
  const getPlanPrice = () => {
    if (plan.period_name === "lifetime") {
      return {
        amount: plan.stripe_plan_price,
        period: "à vie",
        monthlyEquivalent: null,
      };
    }

    if (billingCycle === "monthly") {
      return {
        amount: plan.stripe_plan_price,
        period: "mois",
        monthlyEquivalent: plan.stripe_plan_price,
      };
    } else {
      const monthlyEquivalent = Math.round(plan.stripe_plan_price / 12);
      return {
        amount: plan.stripe_plan_price,
        period: "an",
        monthlyEquivalent,
      };
    }
  };

  const price = getPlanPrice();

  // Calcul du pourcentage d'économie pour l'annuel
  const getSavingsPercentage = () => {
    if (billingCycle === "annual" && plan.period_name === "yearly") {
      return 20;
    }
    return 0;
  };

  // Vérifier si une quantité est "Illimité"
  const isUnlimited = (quantity) => {
    return quantity === UNLIMITED_KEYWORD || quantity === "Illimité";
  };

  // Formater le nom de la feature avec gestion de "Illimité"
  const formatFeatureName = (content) => {
    if (isUnlimited(content.quantity)) {
      return (
        <span className="flex items-center gap-1">
          <span>
            {content.subscription_param_name} illimité
            {content.subscription_param_name.endsWith("s") ? "" : "s"}
          </span>
        </span>
      );
    }

    // Si c'est une quantité numérique
    const quantity = parseFloat(content.quantity);
    if (!isNaN(quantity)) {
      if (quantity > 1) {
        return `${quantity} ${content.subscription_param_name}`;
      }
      if (quantity === 1) {
        return `1 ${content.subscription_param_name}`;
      }
    }

    // Sinon, afficher simplement le nom
    return content.subscription_param_name;
  };

  // Rassembler toutes les features du plan
  const allFeatures = [
    ...(planFeatures.included || []),
    ...(planFeatures.limits || []), // Contient maintenant les features "Illimité"
    ...(planFeatures.params || []),
  ];

  // FAQ
  const faqs = [
    {
      question: "Puis-je annuler à tout moment ?",
      answer:
        "Oui, vous pouvez annuler votre abonnement à tout moment depuis votre espace client.",
    },
    {
      question: "Comment fonctionne la garantie ?",
      answer:
        "Nous offrons une garantie satisfait ou remboursé de 30 jours sans condition.",
    },
    {
      question: "Quels moyens de paiement acceptez-vous ?",
      answer:
        "Nous acceptons toutes les cartes bancaires (Visa, Mastercard, American Express) et PayPal.",
    },
  ];

  const handleConfirmPayment = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await subscriptionService.subscribe(plan.stripe_plan_id);

      if (response && response.url) {
        window.location.href = response.url;
      } else if (response && response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        setError("Réponse invalide du serveur");
      }
    } catch (err) {
      setError("Une erreur est survenue lors de la confirmation du paiement.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isLifetimePlan = () => {
    return plan.period_name === "lifetime";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* En-tête */}
            <div className="flex items-center justify-start gap-4">
              <button
                title="Retour"
                onClick={() => window.history.back()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                Finaliser votre abonnement
              </h1>
            </div>

            {/* Carte de résumé détaillé */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Détails de votre commande
                </h2>
              </div>

              <div className="p-6">
                {/* Plan sélectionné */}
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Plan sélectionné</p>
                      <p className="text-xl font-bold text-gray-900">
                        {plan.name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Prix</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatPrice(price.amount)}
                      <span className="text-sm font-normal text-gray-500 ml-1">
                        /{price.period}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Grille des détails */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">
                      Cycle de facturation
                    </p>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <span className="font-medium text-gray-800">
                        {isLifetimePlan()
                          ? "À vie"
                          : billingCycle === "monthly"
                            ? "Mensuel"
                            : "Annuel"}
                      </span>
                      {billingCycle === "annual" && !isLifetimePlan() && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          -{getSavingsPercentage()}%
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">
                      Début de l'abonnement
                    </p>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-600" />
                      <span className="font-medium text-gray-800">
                        Immédiat après paiement
                      </span>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">
                      Prochains prélèvements
                    </p>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <span className="font-medium text-gray-800">
                        {isLifetimePlan()
                          ? "Aucun (à vie)"
                          : billingCycle === "monthly"
                            ? "Tous les mois"
                            : "Une fois par an"}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Résiliation</p>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-gray-800">
                        Annulable à tout moment
                      </span>
                    </div>
                  </div>
                </div>

                {/* Liste des fonctionnalités */}
                {allFeatures.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">
                      Fonctionnalités incluses
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {allFeatures.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-gray-700 text-sm">
                              {formatFeatureName(feature)}
                            </span>
                            {feature.description && (
                              <p className="text-xs text-gray-500 mt-1">
                                {feature.description}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-600" />
                Questions fréquentes
              </h2>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="border-b border-gray-100 last:border-0 pb-4 last:pb-0"
                  >
                    <p className="font-medium text-gray-800 mb-2">
                      {faq.question}
                    </p>
                    <p className="text-sm text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Colonne latérale */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Résumé de la commande
              </h2>

              {/* Détails du prix */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total</span>
                  <span className="font-medium">
                    {formatPrice(price.amount)}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-800">
                      Total TTC
                    </span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-blue-600">
                        {formatPrice(price.amount)}
                      </span>
                      <span className="text-xs text-gray-500 block">
                        /{price.period}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Badge de sécurité */}
              <SecurityBadge />

              {/* Section Conditions */}
              <label className="flex items-start gap-3 cursor-pointer group mt-6">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600 group-hover:text-gray-900">
                  J'accepte les{" "}
                  <a
                    href="/client/conditions"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    conditions générales de vente
                  </a>{" "}
                  et la{" "}
                  <a
                    href="/client/confidentialite"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    politique de confidentialité
                  </a>
                </span>
              </label>

              {/* Bouton de confirmation */}
              <button
                onClick={handleConfirmPayment}
                disabled={!acceptedTerms || loading}
                className={`w-full mt-6 py-4 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all
                                    ${
                                      !acceptedTerms || loading
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                    }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Préparation du paiement...</span>
                  </>
                ) : (
                  <>
                    <span>Confirmer et payer</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Garanties supplémentaires */}
              <div className="my-6 pt-6 border-t border-gray-100">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Lock className="w-4 h-4 text-green-500" />
                    <span>Paiement SSL crypté</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield className="w-4 h-4 text-green-500" />
                    <span>Garantie 30 jours</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4 text-green-500" />
                    <span>Support 24/7 inclus</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4">
                <span className="text-sm text-gray-500">Besoin d'aide ?</span>
                <a
                  href="mailto:support@exemple.com"
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Mail className="w-5 h-5" />
                </a>
                <a
                  href="tel:+33123456789"
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Phone className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckoutPage;
